import { inject, Inject, Injectable, signal } from '@angular/core';
import {
  AuthFlowType,
  B2BProducts,
  createStytchB2BClient,
  StytchB2BClient,
} from '@stytch/vanilla-js/b2b';
import { getSafeUrl, LocationService } from '@callrail/looky/util';
import { StytchPublicToken } from '../anubis-init/stytch-public-token';
import { ParamMap } from '@angular/router';
import { ErrorReporterService } from '../error-reporter/error-reporter.service';
import { RedirectService } from '../redirect/redirect.service';

interface ClientSetup {
  client: StytchB2BClient;
  config: any;
  presentation: any;
  callbacks: any;
}

@Injectable({
  providedIn: 'root',
})
export class StytchService {
  private readonly client: StytchB2BClient;
  private readonly presentation = {
    theme: {
      'font-family': 'Source Sans Pro, sans-serif',
      'container-radius': '0',
      background: 'var(--color-background-primary)',
      border: 'var(--color-background-primary)',
      primary: 'var(--color-attribluetion)',
      'button-radius': '4px',
      'rounded-base': '2px',
    },
    options: {
      hideHeaderText: true,
    },
  };
  private readonly locationService = inject(LocationService);
  private readonly errorReporter = inject(ErrorReporterService);
  private readonly redirectService = inject(RedirectService);

  private orgId = signal(null);

  constructor(@Inject(StytchPublicToken) publicToken: string) {
    this.client = createStytchB2BClient(publicToken);
  }

  public config(params: ParamMap): ClientSetup {
    const returnToPath =
      params.get('return_to') != null
        ? `?return_to=${encodeURIComponent(getSafeUrl(params.get('return_to')))}`
        : '';
    const loginRedirectUrl = `${this.locationService.origin}/authenticate/login${returnToPath}`;
    return {
      client: this.client,
      config: {
        products: [
          B2BProducts.emailMagicLinks,
          B2BProducts.oauth,
          // B2BProducts.sso,
          B2BProducts.passwords,
        ],
        authFlowType: AuthFlowType.Discovery,
        directLoginForSingleMembership: true,
        disableCreateOrganization: true,
        sessionOptions: {
          sessionDurationMinutes: 60,
        },
        emailMagicLinksOptions: {
          loginRedirectUrl,
          discoveryRedirectUrl: loginRedirectUrl,
        },
        oauthOptions: {
          providers: [{ type: 'google', one_tap: false }],
          loginRedirectUrl,
          discoveryRedirectUrl: loginRedirectUrl,
        },
        passwordOptions: {
          resetPasswordRedirectURL: loginRedirectUrl,
          discoveryRedirectURL: loginRedirectUrl,
        },
      },
      presentation: this.presentation,
      callbacks: this.callbacks(params),
    };
  }

  private callbacks(params: ParamMap) {
    return {
      onEvent: (event) => {
        if (event.type === 'B2B_DISCOVERY_INTERMEDIATE_SESSION_EXCHANGE') {
          this.orgId.set(event.data?.organization?.organization_slug || null);
        }
        if (event.type === 'AUTHENTICATE_FLOW_COMPLETE') {
          if (params.get('return_to')) {
            this.redirectService.redirect(params).subscribe({
              next: ({ redirect_to }) => {
                this.locationService.go(redirect_to, true);
              },
              error: () => {
                this.locationService.go('/', true);
              },
            });
            return;
          }

          if (this.orgId() != null) {
            this.locationService.go(`/analytics/a/${this.orgId()}/home`, true);
          } else {
            this.locationService.go('/', true);
          }
        }
      },
      onError: (error) => {
        this.errorReporter.report(error);
      },
    };
  }
}
